// ─── Google Maps Service (Passage) ─────────────────────────────────────────────
// Handles: Maps JS API loading, stop coordinates, route drawing, place details
// Used by: JourneyScreen map component
// Ported from Yonder's src/services/maps.ts, restyled for Passage:
// warm parchment/sepia map tiles, terracotta route line.

export interface LatLng {
  lat: number
  lng: number
}

export interface MapsStop {
  name: string
  position: LatLng
  placeId?: string
  address?: string
  travelTimeFromPrev?: string   // e.g. "12 mins"
  distanceFromPrev?: string     // e.g. "4.2 km"
  imageUrl?: string             // optional thumbnail for the InfoWindow popup
  label?: string                // optional time-of-day/day label for the InfoWindow popup
}

export interface RouteResult {
  stops: MapsStop[]
  totalDuration: string
  totalDistance: string
}

// ─── Known stop & destination coordinates (curated, avoids geocoding costs) ───
// Covers all 12 Passage destinations (city centre) and every named stop.
const CURATED_COORDS: Record<string, LatLng> = {
  // ── Athens ──────────────────────────────────────────────────────────────────
  'athens':                        { lat: 37.9838, lng: 23.7275 },
  'acropolis parthenon':           { lat: 37.9715, lng: 23.7267 },
  'ancient agora':                 { lat: 37.9755, lng: 23.7224 },
  'national archaeological museum':{ lat: 37.9893, lng: 23.7325 },
  'plaka dinner by lamplight':     { lat: 37.9721, lng: 23.7295 },
  'delphi navel of the world':     { lat: 38.4824, lng: 22.5010 },

  // ── Kyoto ───────────────────────────────────────────────────────────────────
  'kyoto':                         { lat: 35.0116, lng: 135.7681 },
  'fushimi inari before sunrise':  { lat: 34.9671, lng: 135.7727 },
  'kinkakuji golden pavilion':     { lat: 35.0394, lng: 135.7292 },
  'philosophers path nanzenji':    { lat: 35.0116, lng: 135.7960 },
  'kaiseki dinner at your ryokan': { lat: 35.0080, lng: 135.7700 },
  'nara deer and the daibutsu':    { lat: 34.6851, lng: 135.8050 },

  // ── Vienna ──────────────────────────────────────────────────────────────────
  'vienna':                        { lat: 48.2082, lng: 16.3738 },
  'kunsthistorisches museum':      { lat: 48.2033, lng: 16.3616 },
  'schonbrunn palace gardens':     { lat: 48.1847, lng: 16.3122 },
  'vienna state opera':            { lat: 48.2035, lng: 16.3691 },
  'belvedere klimts the kiss':     { lat: 48.1916, lng: 16.3809 },
  'cafe central naschmarkt':       { lat: 48.2103, lng: 16.3676 },

  // ── Dubrovnik ───────────────────────────────────────────────────────────────
  'dubrovnik':                     { lat: 42.6407, lng: 18.1077 },
  'city walls at sunrise':         { lat: 42.6403, lng: 18.1104 },
  'rectors palace':                { lat: 42.6404, lng: 18.1094 },
  'island lokrum by boat':         { lat: 42.6333, lng: 18.1216 },
  'fish dinner above the sea':     { lat: 42.6415, lng: 18.1080 },
  'peljesac peninsula wine road':  { lat: 42.9167, lng: 17.4167 },

  // ── Lisbon ──────────────────────────────────────────────────────────────────
  'lisbon':                        { lat: 38.7223, lng: -9.1393 },
  'jeronimos monastery':           { lat: 38.6979, lng: -9.2062 },
  'belem tower monument':          { lat: 38.6916, lng: -9.2160 },
  'fado in alfama':                { lat: 38.7117, lng: -9.1303 },
  'azulejo tile museum':           { lat: 38.7420, lng: -9.1088 },
  'sintra palaces':                { lat: 38.7973, lng: -9.3944 },

  // ── Edinburgh ───────────────────────────────────────────────────────────────
  'edinburgh':                     { lat: 55.9533, lng: -3.1883 },
  'edinburgh castle':              { lat: 55.9486, lng: -3.1999 },
  'holyrood palace abbey':         { lat: 55.9522, lng: -3.1719 },
  'whisky in the grassmarket':     { lat: 55.9470, lng: -3.1970 },
  'scottish national gallery':     { lat: 55.9508, lng: -3.1958 },
  'rosslyn chapel and the borders':{ lat: 55.8563, lng: -3.1614 },

  // ── Serengeti ────────────────────────────────────────────────────────────────
  'serengeti':                     { lat: -2.3333, lng: 34.8333 },
  'dawn game drive the plains':    { lat: -2.4500, lng: 34.8000 },
  'migration crossing':            { lat: -2.2000, lng: 34.8500 },
  'cheetah on the hunt':           { lat: -2.3000, lng: 34.9000 },
  'sundowner on the plains':       { lat: -2.3500, lng: 34.7500 },
  'ngorongoro crater':             { lat: -3.1620, lng: 35.5866 },

  // ── Patagonia ────────────────────────────────────────────────────────────────
  'patagonia':                     { lat: -50.9423, lng: -73.4068 },
  'torres del paine at sunrise':   { lat: -50.9423, lng: -73.4068 },
  'perito moreno glacier':         { lat: -50.4967, lng: -73.1377 },
  'condor flight above the valley':{ lat: -50.9800, lng: -73.3500 },
  'lago grey by kayak':            { lat: -51.0091, lng: -73.5445 },
  'ushuaia end of the world':      { lat: -54.8019, lng: -68.3030 },

  // ── Morocco ──────────────────────────────────────────────────────────────────
  'morocco':                       { lat: 34.0181, lng: -5.0078 },
  'fes elbali medina':             { lat: 34.0667, lng: -4.9833 },
  'chouara tanneries':             { lat: 34.0672, lng: -4.9758 },
  'ait benhaddou ksar':            { lat: 31.0472, lng: -7.1317 },
  'sahara desert merzouga':        { lat: 31.0801, lng: -4.0128 },
  'marrakech majorelle garden':    { lat: 31.6412, lng: -8.0031 },

  // ── New Zealand ──────────────────────────────────────────────────────────────
  'new zealand':                   { lat: -40.9006, lng: 174.8860 },
  'waitomo glowworm caves':        { lat: -38.2620, lng: 175.1047 },
  'hobbiton movie set':            { lat: -37.8578, lng: 175.6820 },
  'queenstown gondola and lake':   { lat: -45.0312, lng: 168.6626 },
  'milford sound by small ship':   { lat: -44.6413, lng: 167.8972 },
  'rotorua geothermal maori':      { lat: -38.1368, lng: 176.2497 },

  // ── Tuscany ──────────────────────────────────────────────────────────────────
  'tuscany':                       { lat: 43.7711, lng: 11.2486 },
  'florence uffizi gallery':       { lat: 43.7687, lng: 11.2560 },
  'siena il campo and the duomo':  { lat: 43.3188, lng: 11.3308 },
  'san gimignano the tower town':  { lat: 43.4674, lng: 11.0431 },
  'chianti wine road':             { lat: 43.4762, lng: 11.2694 },
  'cooking class in your villa':   { lat: 43.6000, lng: 11.2000 },

  // ── Rajasthan ────────────────────────────────────────────────────────────────
  'rajasthan':                     { lat: 27.0238, lng: 74.2179 },
  'jaipur amber fort at sunrise':  { lat: 26.9855, lng: 75.8513 },
  'udaipur city palace and lake':  { lat: 24.5764, lng: 73.6835 },
  'jodhpur the blue city':         { lat: 26.2916, lng: 73.0201 },
  'pushkar camel fair':            { lat: 26.4899, lng: 74.5511 },
  'village visit and local meal':  { lat: 26.9124, lng: 75.7873 },
}

// City-centre coordinates keyed by destination id (used to initialise/centre the map)
export const DESTINATION_CENTRES: Record<string, LatLng> = {
  athens: CURATED_COORDS['athens'],
  kyoto: CURATED_COORDS['kyoto'],
  vienna: CURATED_COORDS['vienna'],
  dubrovnik: CURATED_COORDS['dubrovnik'],
  lisbon: CURATED_COORDS['lisbon'],
  edinburgh: CURATED_COORDS['edinburgh'],
  serengeti: CURATED_COORDS['serengeti'],
  patagonia: CURATED_COORDS['patagonia'],
  morocco: CURATED_COORDS['morocco'],
  newzealand: CURATED_COORDS['new zealand'],
  tuscany: CURATED_COORDS['tuscany'],
  rajasthan: CURATED_COORDS['rajasthan'],
}

function findCoords(stopName: string): LatLng | null {
  const key = stopName
    .toLowerCase()
    .replace(/[✦🏛🛳🌄👨‍👩‍👧🏺🍽🎭🚂🛏🗓👤💷🗾🦁🏰📖📸🤝✅⛩🎈🦒🦅🐫🎨🧝🚢🏔🕌🏯]/gu, '')
    .replace(/[^\w\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ').slice(0, 6).join(' ')

  // Try progressively shorter matches
  for (const [k, v] of Object.entries(CURATED_COORDS)) {
    if (key.includes(k) || k.includes(key.split(' ').slice(0, 3).join(' '))) return v
  }
  return null
}

// ─── Maps JS API Loader ────────────────────────────────────────────────────────

let mapsPromise: Promise<typeof google.maps> | null = null

export function loadGoogleMaps(): Promise<typeof google.maps> {
  if (mapsPromise) return mapsPromise

  mapsPromise = new Promise((resolve, reject) => {
    // Key resolution: build-time env first (local dev), then runtime config
    // served by the Pages worker (production — dashboard-managed, referrer-restricted).
    const resolveKey = async (): Promise<string> => {
      const buildKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY
      if (buildKey && buildKey !== 'AIza_your_maps_key_here') return buildKey
      const res = await fetch('/api/config')
      if (!res.ok) throw new Error('config unavailable')
      const cfg = await res.json()
      if (!cfg.mapsKey) throw new Error('Google Maps API key not configured')
      return cfg.mapsKey as string
    }

    if (window.google?.maps) {
      resolve(window.google.maps)
      return
    }

    resolveKey().then((key) => {
      const script = document.createElement('script')
      script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places`
      script.async = true
      script.defer = true
      script.onload = () => resolve(window.google.maps)
      script.onerror = () => reject(new Error('Google Maps script failed to load'))
      document.head.appendChild(script)
    }).catch(reject)
  })

  return mapsPromise
}

// ─── Initialise a map on a container element ───────────────────────────────────
// Passage styling: deep espresso/mahogany night-map tones — "the study at dusk",
// keeping the whole journey inside one continuous dark immersive world.

export async function initMap(
  container: HTMLElement,
  center: LatLng,
  zoom = 6
): Promise<google.maps.Map> {
  const maps = await loadGoogleMaps()
  return new maps.Map(container, {
    center,
    zoom,
    disableDefaultUI: true,
    zoomControl: true,
    styles: [
      { elementType: 'geometry', stylers: [{ color: '#1D1108' }] },
      { elementType: 'labels.text.stroke', stylers: [{ color: '#180C05' }] },
      { elementType: 'labels.text.fill', stylers: [{ color: '#C4956A' }] },
      { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
      { featureType: 'transit', stylers: [{ visibility: 'off' }] },
      { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#4A2C1A' }] },
      { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#160B04' }] },
      { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#241209' }] },
      { featureType: 'landscape.natural', elementType: 'geometry', stylers: [{ color: '#2A1710' }] },
      { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#3A2313' }] },
      { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#2A1710' }] },
      { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#D4A017' }] },
      { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#4A2C1A' }] },
    ],
  })
}

// ─── Place numbered markers ─────────────────────────────────────────────────────

// Gold teardrop pin path (Google Maps standard pin silhouette), scaled/anchored
// so the numbered label sits centred in the pin's head — matches Wanderarium's
// teardrop marker treatment, restyled in Passage's gold-on-espresso palette.
const TEARDROP_PATH = 'M 0,0 C -6,0 -10,-4.5 -10,-10 C -10,-15.5 -5.5,-20 0,-20 C 5.5,-20 10,-15.5 10,-10 C 10,-4.5 6,0 0,0 Z'

// Builds the styled InfoWindow HTML content for a stop — dark espresso card with
// a gold accent border, matching Passage's sepia-map palette. Shows the stop photo
// thumbnail (if available), the stop name, and its time-of-day/day label.
function buildInfoWindowContent(stop: MapsStop, index: number): string {
  const imageHtml = stop.imageUrl
    ? `<div style="width:100%;height:96px;background-image:url('${stop.imageUrl}');background-size:cover;background-position:center;border-bottom:1px solid rgba(212,160,23,0.35);"></div>`
    : ''
  const labelHtml = stop.label
    ? `<div style="font-family:'Cinzel',Georgia,serif;font-size:9px;letter-spacing:0.1em;color:#E8C468;text-transform:uppercase;margin-bottom:3px;">${stop.label}</div>`
    : ''
  return `
    <div style="min-width:160px;max-width:200px;background:#241209;border:1px solid rgba(212,160,23,0.5);font-family:'EB Garamond',Georgia,serif;overflow:hidden;">
      ${imageHtml}
      <div style="padding:9px 11px;">
        ${labelHtml}
        <div style="display:flex;align-items:center;gap:6px;">
          <span style="display:inline-flex;align-items:center;justify-content:center;width:16px;height:16px;border-radius:50%;background:#D4A017;color:#241209;font-family:'Cinzel',Georgia,serif;font-size:8px;font-weight:700;flex-shrink:0;">${index + 1}</span>
          <span style="font-family:'Playfair Display',Georgia,serif;font-size:13px;color:#F5EDD8;line-height:1.3;">${stop.name}</span>
        </div>
      </div>
    </div>
  `
}

export async function placeMarkers(
  map: google.maps.Map,
  stops: MapsStop[],
  onStopClick?: (index: number) => void
): Promise<google.maps.Marker[]> {
  const maps = await loadGoogleMaps()
  let openInfoWindow: google.maps.InfoWindow | null = null

  return stops.map((stop, i) => {
    const marker = new maps.Marker({
      position: stop.position,
      map,
      title: stop.name,
      label: {
        text: String(i + 1),
        color: '#241209',
        fontWeight: '700',
        fontSize: '11px',
        fontFamily: 'Cinzel, serif',
      },
      icon: {
        path: TEARDROP_PATH,
        fillColor: '#D4A017',
        fillOpacity: 1,
        strokeColor: '#241209',
        strokeWeight: 1.5,
        scale: 1.6,
        anchor: new maps.Point(0, -10),
        labelOrigin: new maps.Point(0, -11),
      },
    })

    const infoWindow = new maps.InfoWindow({
      content: buildInfoWindowContent(stop, i),
      pixelOffset: new maps.Size(0, -8),
    })

    marker.addListener('click', () => {
      if (openInfoWindow) openInfoWindow.close()
      infoWindow.open(map, marker)
      openInfoWindow = infoWindow
      onStopClick?.(i)
    })

    return marker
  })
}

// ─── Draw route polyline between stops ─────────────────────────────────────────
// Passage: terracotta dashed line, gold arrow markers — an old atlas trade route.

export async function drawRoute(
  map: google.maps.Map,
  stops: MapsStop[]
): Promise<void> {
  const maps = await loadGoogleMaps()
  const path = stops.map((s) => s.position)
  new maps.Polyline({
    path,
    geodesic: true,
    strokeColor: '#8B4513',
    strokeOpacity: 0,
    strokeWeight: 2,
    icons: [{
      icon: {
        path: 'M 0,-1 0,1',
        strokeOpacity: 0.75,
        strokeColor: '#8B4513',
        scale: 3,
      },
      offset: '0',
      repeat: '14px',
    }, {
      icon: { path: maps.SymbolPath.FORWARD_OPEN_ARROW, scale: 3, strokeColor: '#D4A017' },
      offset: '50%',
      repeat: '150px',
    }],
    map,
  })
}

// ─── Fit map bounds to all stops ────────────────────────────────────────────────

export async function fitBounds(
  map: google.maps.Map,
  stops: MapsStop[]
): Promise<void> {
  const maps = await loadGoogleMaps()
  const bounds = new maps.LatLngBounds()
  stops.forEach((s) => bounds.extend(s.position))
  map.fitBounds(bounds, { top: 40, right: 20, bottom: 40, left: 20 })
}

// ─── Resolve stop coordinates (curated first, geocode fallback) ────────────────

export async function resolveStopCoordinates(stopNames: string[], context?: string): Promise<MapsStop[]> {
  const results: MapsStop[] = []

  for (const name of stopNames) {
    const curated = findCoords(name)
    if (curated) {
      results.push({ name, position: curated })
      continue
    }

    // Geocode fallback via Maps Geocoding API
    try {
      const maps = await loadGoogleMaps()
      const geocoder = new maps.Geocoder()
      const cleanName = name.replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/gu, '').split(/[\u2014\u2013-]/)[0].trim()
      const response = await geocoder.geocode({ address: context ? `${cleanName}, ${context}` : cleanName })
      if (response.results.length > 0) {
        const loc = response.results[0].geometry.location
        results.push({
          name,
          position: { lat: loc.lat(), lng: loc.lng() },
          address: response.results[0].formatted_address,
          placeId: response.results[0].place_id,
        })
      }
    } catch {
      console.warn(`Could not geocode stop: ${name}`)
    }
  }

  return results
}

// ─── Get travel times between consecutive stops via Directions API ────────────

export async function getTravelTimes(stops: MapsStop[]): Promise<MapsStop[]> {
  if (stops.length < 2) return stops

  try {
    const maps = await loadGoogleMaps()
    const service = new maps.DirectionsService()
    const waypoints = stops.slice(1, -1).map((s) => ({ location: s.position, stopover: true }))

    const result = await service.route({
      origin: stops[0].position,
      destination: stops[stops.length - 1].position,
      waypoints,
      travelMode: maps.TravelMode.DRIVING,
    })

    const legs = result.routes[0]?.legs ?? []
    return stops.map((stop, i) => ({
      ...stop,
      travelTimeFromPrev: i > 0 ? legs[i - 1]?.duration?.text : undefined,
      distanceFromPrev: i > 0 ? legs[i - 1]?.distance?.text : undefined,
    }))
  } catch {
    return stops
  }
}
